import React, { useEffect, useState } from "react";
import api, { formatDate, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import { Plus, CalendarClock, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { MODULE_CATALOG } from "@/lib/perms";

// Links a login to the employee profile it acts on. Without this link the
// account cannot clock in, file leave or see payslips ("not linked to an
// employee record"). Employees are company-scoped, so list the chosen company's.
function EmployeeLink({ companyId, value, onChange, testId }) {
    const [emps, setEmps] = useState([]);
    useEffect(() => {
        if (!companyId) { setEmps([]); return; }
        let live = true;
        api.get("/hris/employees", { params: { limit: 2000 }, headers: { "X-Company-Id": companyId } })
            .then((r) => { if (live) setEmps(r.data.items || []); })
            .catch(() => { if (live) setEmps([]); });
        return () => { live = false; };
    }, [companyId]);
    return (
        <div className="col-span-2">
            <Label>Linked Employee Profile</Label>
            <select className="w-full h-10 px-3 rounded-md border border-border" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
                <option value="">{companyId ? "Not linked" : "Pick a company first"}</option>
                {value && !emps.some((e) => e.id === value) && <option value={value}>(current link)</option>}
                {emps.map((e) => <option key={e.id} value={e.id}>{e.employee_no} - {e.last_name}, {e.first_name}</option>)}
            </select>
            <div className="text-xs text-muted-foreground mt-1">Needed for clock in, leave, overtime and payslips.</div>
        </div>
    );
}

export function UsersPage() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", password: "Welcome@2026", default_company_id: "", employee_id: "", role_assignments: [] });
    const [resetUser, setResetUser] = useState(null);
    const [newPw, setNewPw] = useState("");

    const doReset = async () => {
        if (!newPw || newPw.length < 8) return toast.error("Password must be at least 8 characters");
        try {
            await api.post(`/admin/users/${resetUser.id}/reset-password`, { new_password: newPw });
            toast.success(`Password reset for ${resetUser.email}`);
            setResetUser(null); setNewPw("");
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const load = async () => {
        const [u, r, c] = await Promise.all([api.get("/admin/users"), api.get("/admin/roles"), api.get("/hris/companies")]);
        setUsers(u.data.items || []); setRoles(r.data || []); setCompanies(c.data || []);
    };
    useEffect(() => { load(); }, []);

    const [editUser, setEditUser] = useState(null);
    const [delUser, setDelUser] = useState(null);

    const doDeleteUser = async () => {
        try {
            await api.delete(`/admin/users/${delUser.id}`);
            toast.success(`Deleted ${delUser.email}`);
            setDelUser(null);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const openEdit = (u) => setEditUser({
        id: u.id, email: u.email, name: u.name || "",
        default_company_id: u.default_company_id || "",
        employee_id: u.employee_id || "",
        is_active: u.is_active !== false,
        role_assignments: (u.roles || []).map((r) => ({ company_id: r.company_id || null, role: r.role })),
    });

    const saveEdit = async () => {
        if (!editUser.role_assignments.length) {
            return toast.error("A user needs at least one role, or they sign in and see nothing.");
        }
        try {
            await api.put(`/admin/users/${editUser.id}`, {
                name: editUser.name,
                default_company_id: editUser.default_company_id || null,
                employee_id: editUser.employee_id || null,
                is_active: editUser.is_active,
                roles: editUser.role_assignments,
            });
            toast.success("Permissions updated");
            setEditUser(null);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const create = async () => {
        try {
            const roles = form.role_assignments.length ? form.role_assignments : [{ company_id: form.default_company_id || null, role: "employee" }];
            await api.post("/admin/users", { ...form, employee_id: form.employee_id || null, roles });
            toast.success("User created");
            setOpen(false); setForm({ name: "", email: "", password: "Welcome@2026", default_company_id: "", employee_id: "", role_assignments: [] });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader
                title="Users & Roles"
                subtitle="Manage user accounts with multi-role access across companies."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-2" data-testid="new-user-btn"><Plus className="size-4" /> New User</button>
                        </DialogTrigger>
                        <DialogContent className="max-w-xl">
                            <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
                                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
                                <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
                                <div>
                                    <Label>Default Company</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.default_company_id} onChange={(e) => setForm({ ...form, default_company_id: e.target.value })}>
                                        <option value="">—</option>
                                        {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <EmployeeLink companyId={form.default_company_id || form.role_assignments.find((r) => r.company_id)?.company_id || ""} value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} testId="user-employee" />
                                <div className="col-span-2">
                                    <Label>Role Assignments</Label>
                                    <div className="space-y-1">
                                        {form.role_assignments.map((ra, i) => (
                                            <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                                <select className="col-span-6 h-9 px-2 rounded-md border border-border text-sm" value={ra.company_id || ""} onChange={(e) => { const n = [...form.role_assignments]; n[i] = { ...n[i], company_id: e.target.value || null }; setForm({ ...form, role_assignments: n }); }}>
                                                    <option value="">All companies (tenant)</option>
                                                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                                <select className="col-span-5 h-9 px-2 rounded-md border border-border text-sm" value={ra.role} onChange={(e) => { const n = [...form.role_assignments]; n[i] = { ...n[i], role: e.target.value }; setForm({ ...form, role_assignments: n }); }}>
                                                    {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                                                </select>
                                                <button className="col-span-1 text-destructive text-xs" onClick={() => setForm({ ...form, role_assignments: form.role_assignments.filter((_, j) => j !== i) })}>×</button>
                                            </div>
                                        ))}
                                        <button className="text-xs text-primary" onClick={() => setForm({ ...form, role_assignments: [...form.role_assignments, { company_id: companies[0]?.id || null, role: "employee" }] })} data-testid="add-role-btn">+ Add role</button>
                                    </div>
                                </div>
                            </div>
                            <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={create} data-testid="user-create-submit">Create User</button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <TableWrap>
                <thead><tr><th>Name</th><th>Email</th><th>Roles</th><th>Active</th><th>Actions</th></tr></thead>
                <tbody>
                    {users.map((u) => (
                        <tr key={u.id} data-testid={`user-row-${u.email}`}>
                            <td>{u.name}</td>
                            <td className="font-mono text-xs">{u.email}</td>
                            <td>
                                <div className="flex flex-wrap gap-1">
                                    {(u.roles || []).map((r, i) => {
                                        const c = companies.find((x) => x.id === r.company_id);
                                        return <span key={i} className="badge badge-info">{r.role}{c ? ` · ${c.code}` : ""}</span>;
                                    })}
                                </div>
                            </td>
                            <td>{u.is_active ? <span className="badge badge-approved">active</span> : <span className="badge badge-locked">disabled</span>}</td>
                            <td>
                                <div className="flex gap-2">
                                    <button
                                        className="h-8 px-2 rounded-md border border-border text-xs hover:bg-secondary"
                                        onClick={() => openEdit(u)}
                                        data-testid={`edit-user-${u.email}`}
                                    >Edit Permissions</button>
                                    <button
                                        className="h-8 px-2 rounded-md border border-border text-xs hover:bg-secondary"
                                        onClick={() => { setResetUser(u); setNewPw(""); }}
                                        data-testid={`reset-pw-${u.email}`}
                                    >Reset Password</button>
                                    <button
                                        className="h-8 px-2 rounded-md border border-destructive/40 text-destructive text-xs hover:bg-destructive/10"
                                        onClick={() => setDelUser(u)}
                                        data-testid={`delete-user-${u.email}`}
                                    >Delete</button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </TableWrap>

            <Dialog open={!!delUser} onOpenChange={(o) => { if (!o) setDelUser(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Delete User</DialogTitle></DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div>
                            Permanently delete the sign-in account for{" "}
                            <span className="font-mono text-foreground">{delUser?.email}</span>?
                        </div>
                        <div className="text-muted-foreground">
                            This cannot be undone. The audit trail is kept - it records the
                            email as text, so who did what survives. If you only want to stop
                            them signing in, use Edit Permissions and clear Active instead.
                        </div>
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md border border-border text-sm" onClick={() => setDelUser(null)}>Cancel</button>
                        <button className="h-9 px-3 rounded-md bg-destructive text-white text-sm" onClick={doDeleteUser} data-testid="confirm-delete-user">Delete Account</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
                <DialogContent className="max-w-xl">
                    <DialogHeader><DialogTitle>Edit Permissions</DialogTitle></DialogHeader>
                    {editUser && (
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2 text-sm text-muted-foreground">
                            <span className="font-mono text-foreground">{editUser.email}</span>
                            {" "}- the sign-in address cannot be changed here.
                        </div>
                        <div><Label>Name</Label><Input value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="edit-user-name" /></div>
                        <div>
                            <Label>Default Company</Label>
                            <select className="w-full h-10 px-3 rounded-md border border-border" value={editUser.default_company_id || ""} onChange={(e) => setEditUser({ ...editUser, default_company_id: e.target.value })} data-testid="edit-user-company">
                                <option value="">-</option>
                                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <EmployeeLink companyId={editUser.default_company_id || editUser.role_assignments.find((r) => r.company_id)?.company_id || ""} value={editUser.employee_id} onChange={(v) => setEditUser({ ...editUser, employee_id: v })} testId="edit-user-employee" />
                        <div className="col-span-2 flex items-center gap-2">
                            <input id="edit-active" type="checkbox" checked={editUser.is_active} onChange={(e) => setEditUser({ ...editUser, is_active: e.target.checked })} data-testid="edit-user-active" />
                            <Label htmlFor="edit-active">Active - an inactive user cannot sign in</Label>
                        </div>
                        <div className="col-span-2">
                            <Label>Role Assignments</Label>
                            <div className="space-y-1">
                                {editUser.role_assignments.map((ra, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                        <select className="col-span-6 h-9 px-2 rounded-md border border-border text-sm" value={ra.company_id || ""} onChange={(e) => { const n = [...editUser.role_assignments]; n[i] = { ...n[i], company_id: e.target.value || null }; setEditUser({ ...editUser, role_assignments: n }); }}>
                                            <option value="">All companies (tenant)</option>
                                            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                        <select className="col-span-5 h-9 px-2 rounded-md border border-border text-sm" value={ra.role} onChange={(e) => { const n = [...editUser.role_assignments]; n[i] = { ...n[i], role: e.target.value }; setEditUser({ ...editUser, role_assignments: n }); }} data-testid={`edit-role-${i}`}>
                                            {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                                        </select>
                                        <button className="col-span-1 text-destructive text-xs" onClick={() => setEditUser({ ...editUser, role_assignments: editUser.role_assignments.filter((_, j) => j !== i) })}>x</button>
                                    </div>
                                ))}
                                <button className="text-xs text-primary" onClick={() => setEditUser({ ...editUser, role_assignments: [...editUser.role_assignments, { company_id: companies[0]?.id || null, role: "employee" }] })} data-testid="edit-add-role">+ Add role</button>
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">
                                What each role can reach is set in Roles &amp; Permissions. This decides which roles this person holds, and in which company.
                            </div>
                        </div>
                    </div>
                    )}
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveEdit} data-testid="edit-user-save">Save Changes</button></DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) { setResetUser(null); setNewPw(""); } }}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">
                            Set a new password for <span className="font-mono text-foreground">{resetUser?.email}</span>.
                        </div>
                        <div>
                            <Label>New Password</Label>
                            <Input
                                type="text"
                                value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                placeholder="Min. 8 characters"
                                data-testid="reset-pw-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={doReset} data-testid="reset-pw-submit">Reset Password</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export function CompaniesPage() {
    const { user, refresh } = useAuth();
    const isSuper = (user?.roles || []).some((r) => r.role === "super_admin");
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", code: "", legal_name: "", tin: "", rdo_code: "", sss_employer_no: "", philhealth_employer_no: "", pagibig_employer_no: "" });
    const [moduleCompany, setModuleCompany] = useState(null);
    const [editCo, setEditCo] = useState(null);
    const [delCo, setDelCo] = useState(null);

    const openEditCo = (c) => setEditCo({
        id: c.id, name: c.name || "", code: c.code || "", legal_name: c.legal_name || "",
        tin: c.tin || "", rdo_code: c.rdo_code || "", address: c.address || "",
        sss_employer_no: c.sss_employer_no || "", philhealth_employer_no: c.philhealth_employer_no || "",
        pagibig_employer_no: c.pagibig_employer_no || "",
        payroll_frequency: c.payroll_frequency || "semi_monthly",
        is_active: c.is_active !== false,
        payslip_password_protect: !!c.payslip_password_protect,
    });

    const saveCo = async () => {
        try {
            const { id, ...body } = editCo;
            await api.put(`/hris/companies/${id}`, body);
            toast.success("Company updated");
            setEditCo(null); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const doDeleteCo = async () => {
        try {
            await api.delete(`/hris/companies/${delCo.id}`);
            toast.success(`Deleted ${delCo.name}`);
            setDelCo(null); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const [disabledSet, setDisabledSet] = useState([]);
    const load = async () => { const { data } = await api.get("/hris/companies"); setItems(data || []); };
    useEffect(() => { load(); }, []);
    const create = async () => {
        try { await api.post("/hris/companies", form); toast.success("Company created"); setOpen(false); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const openModules = (c) => { setModuleCompany(c); setDisabledSet([...(c.disabled_modules || [])]); };
    const toggleModule = (key) => {
        setDisabledSet((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
    };
    const saveModules = async () => {
        try {
            await api.put(`/admin/companies/${moduleCompany.id}/modules`, { disabled_modules: disabledSet });
            toast.success("Modules updated");
            setModuleCompany(null);
            await load();
            await refresh(); // refresh /me so the sidebar reflects new module visibility
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    return (
        <div>
            <PageHeader
                title="Companies"
                subtitle="Companies under this tenant. Each has its own payroll, policies, and compliance filings."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild><button className="h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-2" data-testid="new-company-btn"><Plus className="size-4" /> New Company</button></DialogTrigger>
                        <DialogContent className="max-w-xl">
                            <DialogHeader><DialogTitle>New Company</DialogTitle></DialogHeader>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" /></div>
                                <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="company-code-input" /></div>
                                <div className="col-span-2"><Label>Legal Name</Label><Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="company-legal-name-input" /></div>
                                <div><Label>TIN</Label><Input value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} data-testid="company-tin-input" /></div>
                                <div><Label>RDO Code</Label><Input value={form.rdo_code} onChange={(e) => setForm({ ...form, rdo_code: e.target.value })} data-testid="company-rdo-input" /></div>
                                <div><Label>SSS Employer No.</Label><Input value={form.sss_employer_no} onChange={(e) => setForm({ ...form, sss_employer_no: e.target.value })} data-testid="company-sss-input" /></div>
                                <div><Label>PhilHealth Employer No.</Label><Input value={form.philhealth_employer_no} onChange={(e) => setForm({ ...form, philhealth_employer_no: e.target.value })} data-testid="company-philhealth-input" /></div>
                                <div><Label>Pag-IBIG Employer No.</Label><Input value={form.pagibig_employer_no} onChange={(e) => setForm({ ...form, pagibig_employer_no: e.target.value })} data-testid="company-pagibig-input" /></div>
                            </div>
                            <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={create} data-testid="company-create-submit">Create</button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <TableWrap>
                <thead><tr><th>Code</th><th>Name</th><th>Legal Name</th><th>TIN</th><th>Modules</th>{isSuper && <th>Actions</th>}</tr></thead>
                <tbody>
                    {items.map((c) => {
                        const disabledCount = (c.disabled_modules || []).length;
                        return (<tr key={c.id} data-testid={`company-${c.code}`}>
                            <td className="font-mono">{c.code}</td><td className="font-medium">{c.name}</td><td>{c.legal_name || "—"}</td>
                            <td className="font-mono text-xs">{c.tin || "—"}</td>
                            <td className="text-xs">
                                {disabledCount === 0
                                    ? <span className="badge badge-approved">All enabled</span>
                                    : <span className="badge badge-draft">{MODULE_CATALOG.length - disabledCount}/{MODULE_CATALOG.length} enabled</span>}
                            </td>
                            {isSuper && (
                                <td>
                                    <div className="flex gap-2">
                                        <button className="h-8 px-2 rounded-md border border-border text-xs hover:bg-secondary" onClick={() => openEditCo(c)} data-testid={`edit-company-${c.code}`}>Edit</button>
                                        <button className="h-8 px-2 rounded-md border border-border text-xs hover:bg-secondary" onClick={() => openModules(c)} data-testid={`manage-modules-${c.code}`}>Manage Modules</button>
                                        <button className="h-8 px-2 rounded-md border border-destructive/40 text-destructive text-xs hover:bg-destructive/10" onClick={() => setDelCo(c)} data-testid={`delete-company-${c.code}`}>Delete</button>
                                    </div>
                                </td>
                            )}
                        </tr>);
                    })}
                </tbody>
            </TableWrap>

            <Dialog open={!!editCo} onOpenChange={(o) => { if (!o) setEditCo(null); }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Edit Company</DialogTitle></DialogHeader>
                    {editCo && (
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Name</Label><Input value={editCo.name} onChange={(e) => setEditCo({ ...editCo, name: e.target.value })} data-testid="co-name" /></div>
                        <div><Label>Code</Label><Input value={editCo.code} onChange={(e) => setEditCo({ ...editCo, code: e.target.value })} data-testid="co-code" /></div>
                        <div className="col-span-2"><div><Label>Legal Name</Label><Input value={editCo.legal_name} onChange={(e) => setEditCo({ ...editCo, legal_name: e.target.value })} data-testid="co-legal_name" /></div></div>
                        <div><Label>TIN</Label><Input value={editCo.tin} onChange={(e) => setEditCo({ ...editCo, tin: e.target.value })} data-testid="co-tin" /></div>
                        <div><Label>RDO Code</Label><Input value={editCo.rdo_code} onChange={(e) => setEditCo({ ...editCo, rdo_code: e.target.value })} data-testid="co-rdo_code" /></div>
                        <div className="col-span-2"><div><Label>Address</Label><Input value={editCo.address} onChange={(e) => setEditCo({ ...editCo, address: e.target.value })} data-testid="co-address" /></div></div>
                        <div><Label>SSS Employer No.</Label><Input value={editCo.sss_employer_no} onChange={(e) => setEditCo({ ...editCo, sss_employer_no: e.target.value })} data-testid="co-sss_employer_no" /></div>
                        <div><Label>PhilHealth Employer No.</Label><Input value={editCo.philhealth_employer_no} onChange={(e) => setEditCo({ ...editCo, philhealth_employer_no: e.target.value })} data-testid="co-philhealth_employer_no" /></div>
                        <div><Label>Pag-IBIG Employer No.</Label><Input value={editCo.pagibig_employer_no} onChange={(e) => setEditCo({ ...editCo, pagibig_employer_no: e.target.value })} data-testid="co-pagibig_employer_no" /></div>
                        <div>
                            <Label>Payroll Frequency</Label>
                            <select className="w-full h-10 px-3 rounded-md border border-border" value={editCo.payroll_frequency} onChange={(e) => setEditCo({ ...editCo, payroll_frequency: e.target.value })} data-testid="co-frequency">
                                <option value="semi_monthly">Semi-monthly</option>
                                <option value="monthly">Monthly</option>
                                <option value="weekly">Weekly</option>
                            </select>
                        </div>
                        <div className="col-span-2 flex items-center gap-2">
                            <input id="co-active" type="checkbox" checked={editCo.is_active} onChange={(e) => setEditCo({ ...editCo, is_active: e.target.checked })} data-testid="co-active" />
                            <Label htmlFor="co-active">Active - deactivate instead of deleting a company that has payroll history</Label>
                        </div>
                        <div className="col-span-2 flex items-start gap-2">
                            <input id="co-payslip-pw" className="mt-1" type="checkbox" checked={!!editCo.payslip_password_protect} onChange={(e) => setEditCo({ ...editCo, payslip_password_protect: e.target.checked })} data-testid="co-payslip-pw" />
                            <Label htmlFor="co-payslip-pw">Password-protect payslip PDFs. Employees open them with their birth date (MMDDYYYY), or their employee number if none is on file. Off by default.</Label>
                        </div>
                        <div className="col-span-2 text-xs text-muted-foreground">
                            TIN, RDO and the employer numbers appear on BIR and remittance filings, so a typo here reaches the forms.
                        </div>
                    </div>
                    )}
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveCo} data-testid="co-save">Save Changes</button></DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!delCo} onOpenChange={(o) => { if (!o) setDelCo(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Delete Company</DialogTitle></DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div>Permanently delete <span className="font-medium text-foreground">{delCo?.name}</span>?</div>
                        <div className="text-muted-foreground">
                            Only an empty company can be deleted. If it holds employees,
                            attendance or payroll, the server will refuse and tell you what is
                            still attached - payroll and statutory records have to be retained.
                            Deactivate it instead in that case.
                        </div>
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md border border-border text-sm" onClick={() => setDelCo(null)}>Cancel</button>
                        <button className="h-9 px-3 rounded-md bg-destructive text-white text-sm" onClick={doDeleteCo} data-testid="confirm-delete-company">Delete Company</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!moduleCompany} onOpenChange={(o) => { if (!o) setModuleCompany(null); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Modules — {moduleCompany?.name}</DialogTitle></DialogHeader>
                    <div className="text-sm text-muted-foreground mb-2">Enabled modules appear in the sidebar for this company. Toggle off to hide a module.</div>
                    <div className="grid grid-cols-2 gap-2 max-h-[50vh] overflow-auto">
                        {MODULE_CATALOG.map((m) => {
                            const enabled = !disabledSet.includes(m.key);
                            return (
                                <label key={m.key} className="flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2 cursor-pointer" data-testid={`module-row-${m.key}`}>
                                    <input type="checkbox" checked={enabled} onChange={() => toggleModule(m.key)} data-testid={`module-toggle-${m.key}`} />
                                    <span className={enabled ? "" : "text-muted-foreground line-through"}>{m.label}</span>
                                </label>
                            );
                        })}
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md border border-border text-sm" onClick={() => setDisabledSet([])} data-testid="modules-enable-all">Enable all</button>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveModules} data-testid="modules-save">Save</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export function AuditPage() {
    const [items, setItems] = useState([]);
    const [entity, setEntity] = useState("");
    const load = async () => {
        const params = {}; if (entity) params.entity = entity;
        const { data } = await api.get("/audit", { params });
        setItems(data || []);
    };
    useEffect(() => { load(); }, [entity]);
    return (
        <div>
            <PageHeader title="Audit Log" subtitle="Immutable history of all sensitive changes — payroll, employees, government IDs, approvals." />
            <Surface className="p-3 mb-3">
                <select value={entity} onChange={(e) => setEntity(e.target.value)} className="h-9 px-3 rounded-md border border-border text-sm" data-testid="audit-entity-filter">
                    <option value="">All entities</option>
                    <option value="payroll_run">Payroll Run</option>
                    <option value="employee">Employee</option>
                    <option value="final_pay">Final Pay</option>
                    <option value="user">User</option>
                    <option value="leave">Leave</option>
                    <option value="overtime">Overtime</option>
                </select>
            </Surface>
            <TableWrap>
                <thead><tr><th>Timestamp</th><th>User</th><th>Action</th><th>Entity</th><th>Entity ID</th><th>Reason</th></tr></thead>
                <tbody>
                    {items.map((a) => (
                        <tr key={a.id} data-testid={`audit-${a.id}`}>
                            <td className="font-mono text-xs">{formatDate(a.created_at)}</td>
                            <td className="font-mono text-xs">{a.user_email || "—"}</td>
                            <td><span className="badge badge-info">{a.action}</span></td>
                            <td>{a.entity}</td>
                            <td className="font-mono text-xs">{a.entity_id?.slice(0, 8)}</td>
                            <td className="text-xs">{a.reason || "—"}</td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={6}><Empty title="No audit entries" /></td></tr>}
                </tbody>
            </TableWrap>
        </div>
    );
}

const WO_STATUS_BADGE = {
    scheduled: "badge badge-draft",
    partially_effective: "badge badge-pending",
    fully_effective: "badge badge-approved",
};

function tranchePill(t) {
    if (t.status === "effective") return <span className="badge badge-approved">effective</span>;
    if (t.days_until === 0) return <span className="badge badge-pending">today</span>;
    return <span className="badge badge-draft">in {t.days_until}d</span>;
}

function WageOrdersSection() {
    const [orders, setOrders] = useState([]);
    const [open, setOpen] = useState(false);
    const emptyForm = { region: "NCR", order_no: "", title: "", base_daily_minimum: "", notes: "", tranches: [{ increase: "", effective_date: "" }] };
    const [form, setForm] = useState(emptyForm);

    const load = async () => {
        try { const { data } = await api.get("/wage-orders"); setOrders(data.items || []); }
        catch (e) { /* view may be gated */ }
    };
    useEffect(() => { load(); }, []);

    const setTranche = (i, k, v) => setForm((f) => ({ ...f, tranches: f.tranches.map((t, j) => j === i ? { ...t, [k]: v } : t) }));
    const addTranche = () => setForm((f) => ({ ...f, tranches: [...f.tranches, { increase: "", effective_date: "" }] }));
    const removeTranche = (i) => setForm((f) => ({ ...f, tranches: f.tranches.filter((_, j) => j !== i) }));

    const save = async () => {
        if (!form.region.trim()) return toast.error("Region is required");
        if (!form.base_daily_minimum) return toast.error("Base daily minimum is required");
        const tranches = form.tranches
            .filter((t) => t.increase && t.effective_date)
            .map((t) => ({ increase: Number(t.increase), effective_date: t.effective_date }));
        if (!tranches.length) return toast.error("Add at least one tranche (increase + effective date)");
        try {
            await api.post("/wage-orders", {
                region: form.region, order_no: form.order_no, title: form.title,
                base_daily_minimum: Number(form.base_daily_minimum), notes: form.notes, tranches,
            });
            toast.success("Wage order scheduled");
            setOpen(false); setForm(emptyForm); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const remove = async (id, label) => {
        if (!window.confirm(`Delete ${label}? Payroll will stop applying its scheduled increases.`)) return;
        try { await api.delete(`/wage-orders/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    return (
        <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <CalendarClock className="size-4 text-primary" />
                    <h3 className="font-display font-semibold">Scheduled Wage Orders</h3>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="new-wageorder-btn"><Plus className="size-4" /> Schedule Wage Order</button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Schedule a Wage Order</DialogTitle></DialogHeader>
                        <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                                <div><Label>Region</Label><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="NCR" data-testid="wo-region" /></div>
                                <div><Label>Base Daily Minimum (₱)</Label><Input type="number" value={form.base_daily_minimum} onChange={(e) => setForm({ ...form, base_daily_minimum: e.target.value })} placeholder="695" data-testid="wo-base" /></div>
                            </div>
                            <div><Label>Order No.</Label><Input value={form.order_no} onChange={(e) => setForm({ ...form, order_no: e.target.value })} placeholder="Wage Order No. NCR-27" /></div>
                            <div className="pt-1">
                                <div className="flex items-center justify-between"><Label>Tranches</Label><button className="text-xs text-primary flex items-center gap-1" onClick={addTranche}><Plus className="size-3" /> Add</button></div>
                                {form.tranches.map((t, i) => (
                                    <div key={i} className="flex items-end gap-2 mt-1">
                                        <div className="flex-1"><Label className="text-xs">Increase (₱)</Label><Input type="number" value={t.increase} onChange={(e) => setTranche(i, "increase", e.target.value)} placeholder="60" data-testid={`wo-tranche-inc-${i}`} /></div>
                                        <div className="flex-1"><Label className="text-xs">Effective date</Label><Input type="date" value={t.effective_date} onChange={(e) => setTranche(i, "effective_date", e.target.value)} data-testid={`wo-tranche-date-${i}`} /></div>
                                        {form.tranches.length > 1 && <button className="h-9 px-2 text-destructive" onClick={() => removeTranche(i)}><Trash2 className="size-4" /></button>}
                                    </div>
                                ))}
                            </div>
                            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="RTWPB reference / details" /></div>
                        </div>
                        <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={save} data-testid="wo-save">Schedule</button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
            {!orders.length && <Surface className="p-4"><Empty title="No wage orders scheduled" subtitle="Schedule an RTWPB wage order to auto-apply minimum-wage increases on their effective dates." /></Surface>}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {orders.map((o) => (
                    <Surface key={o.id} className="p-4" data-testid={`wageorder-${o.id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="font-display font-semibold">{o.title || o.order_no}</div>
                                <div className="text-xs text-muted-foreground font-mono">{o.region} · base ₱{Number(o.base_daily_minimum).toFixed(2)} → ₱{Number(o.full_floor).toFixed(2)}</div>
                            </div>
                            <span className={WO_STATUS_BADGE[o.status] || "badge badge-draft"}>{(o.status || "").replace(/_/g, " ")}</span>
                        </div>
                        <div className="mt-2 text-xs">
                            <div className="flex items-center justify-between text-muted-foreground"><span>Current effective floor</span><span className="font-semibold text-foreground">₱{Number(o.current_floor).toFixed(2)}</span></div>
                        </div>
                        <table className="w-full dense-table mt-2">
                            <thead><tr><th>#</th><th className="num">Increase</th><th>Effective</th><th className="num">New Floor</th><th>Status</th></tr></thead>
                            <tbody>
                                {o.tranches.map((t) => (
                                    <tr key={t.seq}>
                                        <td>{t.seq}</td>
                                        <td className="num">+₱{Number(t.increase).toFixed(2)}</td>
                                        <td className="font-mono text-xs">{formatDate(t.effective_date)}</td>
                                        <td className="num font-semibold">₱{Number(t.resulting_floor).toFixed(2)}</td>
                                        <td>{tranchePill(t)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {o.notes && <div className="text-xs text-muted-foreground mt-2">{o.notes}</div>}
                        <div className="mt-2 text-right"><button className="text-xs text-destructive" onClick={() => remove(o.id, o.title || o.order_no)}>Delete</button></div>
                    </Surface>
                ))}
            </div>
        </div>
    );
}

export function MinimumWagePage() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ region: "", daily_minimum: "", effective_date: "", note: "" });

    const load = async () => {
        try { const { data } = await api.get("/admin/minimum-wages"); setItems(data.items || []); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load"); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.region.trim()) return toast.error("Region is required");
        if (!form.daily_minimum) return toast.error("Daily minimum is required");
        try {
            await api.put("/admin/minimum-wages", { ...form, daily_minimum: Number(form.daily_minimum) });
            toast.success("Minimum wage saved");
            setOpen(false); setForm({ region: "", daily_minimum: "", effective_date: "", note: "" });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const edit = (row) => { setForm({ region: row.region, daily_minimum: row.daily_minimum, effective_date: row.effective_date || "", note: row.note || "" }); setOpen(true); };

    const remove = async (region) => {
        if (!window.confirm(`Remove custom minimum wage for ${region}? It will revert to the built-in default.`)) return;
        try { await api.delete(`/admin/minimum-wages/${region}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    return (
        <div>
            <PageHeader
                title="Minimum Wage"
                subtitle="Schedule RTWPB wage orders with effective-dated tranches. Payroll auto-lifts below-minimum earners to the floor in force on each work-date."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="new-minwage-btn"><Plus className="size-4" /> Set Region Rate</button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Set Regional Minimum Wage</DialogTitle></DialogHeader>
                            <div className="space-y-2">
                                <div><Label>Region</Label><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. NCR, REGION_III" data-testid="minwage-region" /></div>
                                <div><Label>Daily Minimum (₱)</Label><Input type="number" value={form.daily_minimum} onChange={(e) => setForm({ ...form, daily_minimum: e.target.value })} data-testid="minwage-amount" /></div>
                                <div><Label>Effective Date</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></div>
                                <div><Label>Note (wage order ref)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Wage Order NCR-25" /></div>
                            </div>
                            <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={save} data-testid="minwage-save">Save</button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <WageOrdersSection />
            <h3 className="font-display font-semibold mb-2">Regional Floor Overrides</h3>
            <TableWrap>
                <thead><tr><th>Region</th><th className="num">Daily Minimum</th><th>Effective</th><th>Source</th><th>Note</th><th></th></tr></thead>
                <tbody>
                    {items.map((r) => (
                        <tr key={r.region} data-testid={`minwage-row-${r.region}`}>
                            <td className="font-mono">{r.region}</td>
                            <td className="num font-semibold">₱{Number(r.daily_minimum).toFixed(2)}</td>
                            <td className="font-mono text-xs">{r.effective_date || "—"}</td>
                            <td>{r.source === "custom" ? <span className="badge badge-approved">custom</span> : <span className="badge badge-draft">default</span>}</td>
                            <td className="text-xs">{r.note || "—"}</td>
                            <td>
                                <div className="flex items-center gap-2">
                                    <button className="text-xs text-primary" onClick={() => edit(r)}>{r.source === "custom" ? "Edit" : "Override"}</button>
                                    {r.source === "custom" && <button className="text-xs text-destructive" onClick={() => remove(r.region)}>Remove</button>}
                                </div>
                            </td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={6}><Empty title="No minimum wage rows" /></td></tr>}
                </tbody>
            </TableWrap>
        </div>
    );
}

export function LeavePoliciesPage() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ leave_type: "", name: "", annual_days: "", accrual_method: "annual", carryover_cap: "", convertible: false, max_convertible_days: "", prorate_first_year: true });

    const load = async () => {
        try { const { data } = await api.get("/leave/policies"); setItems(data.items || []); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load"); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.leave_type.trim()) return toast.error("Leave type is required");
        if (form.annual_days === "") return toast.error("Annual days is required");
        try {
            await api.put("/leave/policies", { ...form, annual_days: Number(form.annual_days), carryover_cap: Number(form.carryover_cap || 0), max_convertible_days: Number(form.max_convertible_days || 0) });
            toast.success("Policy saved");
            setOpen(false); setForm({ leave_type: "", name: "", annual_days: "", accrual_method: "annual", carryover_cap: "", convertible: false, max_convertible_days: "", prorate_first_year: true });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const edit = (p) => { setForm({ leave_type: p.leave_type, name: p.name, annual_days: p.annual_days, accrual_method: p.accrual_method, carryover_cap: p.carryover_cap || "", convertible: !!p.convertible, max_convertible_days: p.max_convertible_days || "", prorate_first_year: p.prorate_first_year !== false }); setOpen(true); };

    return (
        <div>
            <PageHeader
                title="Leave Policies"
                subtitle="Company leave that accrues above the statutory minimum (SIL 5 days). Configure days, accrual, carry-over cap and cash-convertibility."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="new-leavepolicy-btn"><Plus className="size-4" /> Set Policy</button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Leave Policy</DialogTitle></DialogHeader>
                            <div className="space-y-2">
                                <div><Label>Leave Type (key)</Label><Input value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} placeholder="e.g. vacation, sick" data-testid="lp-type" /></div>
                                <div><Label>Display Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Vacation Leave" /></div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div><Label>Annual Days</Label><Input type="number" value={form.annual_days} onChange={(e) => setForm({ ...form, annual_days: e.target.value })} data-testid="lp-days" /></div>
                                    <div><Label>Carry-over Cap</Label><Input type="number" value={form.carryover_cap} onChange={(e) => setForm({ ...form, carryover_cap: e.target.value })} /></div>
                                </div>
                                <div>
                                    <Label>Accrual Method</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.accrual_method} onChange={(e) => setForm({ ...form, accrual_method: e.target.value })} data-testid="lp-method">
                                        <option value="annual">Annual (granted at year start)</option>
                                        <option value="monthly">Monthly (accrues each month)</option>
                                    </select>
                                </div>
                                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.convertible} onChange={(e) => setForm({ ...form, convertible: e.target.checked })} data-testid="lp-convertible" /> Convertible to cash</label>
                                {form.convertible && (
                                    <div><Label>Max Convertible Days (0 = no limit)</Label><Input type="number" min={0} value={form.max_convertible_days} onChange={(e) => setForm({ ...form, max_convertible_days: e.target.value })} placeholder="e.g. 10" data-testid="lp-max-convertible" /></div>
                                )}
                                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.prorate_first_year} onChange={(e) => setForm({ ...form, prorate_first_year: e.target.checked })} /> Pro-rate first year (by hire month)</label>
                            </div>
                            <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={save} data-testid="lp-save">Save Policy</button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <TableWrap>
                <thead><tr><th>Leave</th><th className="num">Annual</th><th>Accrual</th><th className="num">Carry-over Cap</th><th>Convertible</th><th className="num">Max Cash Days</th><th>Source</th><th></th></tr></thead>
                <tbody>
                    {items.map((p) => (
                        <tr key={p.leave_type} data-testid={`lp-row-${p.leave_type}`}>
                            <td>{p.name} <span className="text-xs text-muted-foreground font-mono">({p.leave_type})</span></td>
                            <td className="num font-semibold">{p.annual_days}</td>
                            <td className="capitalize">{p.accrual_method}</td>
                            <td className="num">{p.carryover_cap || 0}</td>
                            <td>{p.convertible ? <span className="badge badge-approved">yes</span> : <span className="badge badge-draft">no</span>}</td>
                            <td className="num">{p.convertible ? (p.max_convertible_days ? p.max_convertible_days : "no limit") : "—"}</td>
                            <td>{p.source === "custom" ? <span className="badge badge-approved">custom</span> : <span className="badge badge-draft">default</span>}</td>
                            <td><button className="text-xs text-primary" onClick={() => edit(p)}>{p.source === "custom" ? "Edit" : "Override"}</button></td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={8}><Empty title="No leave policies" /></td></tr>}
                </tbody>
            </TableWrap>
        </div>
    );
}
